import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getAPIBaseURL } from '../services/helpers'
import { getData, postData, postDataWithForm } from '../services/request'
import { useInterviewLeaveGuard } from './InterviewLeaveGuardContext'
import { appendReferralAttribution } from '../services/referralAttribution'

// Keep interview videos small enough for typical hosting limits.
const INTERVIEW_MEDIA_CONSTRAINTS = {
  video: {
    width: { ideal: 640, max: 1280 },
    height: { ideal: 480, max: 720 },
    frameRate: { ideal: 24, max: 30 }
  },
  audio: true
}

const formatRecordingSize = (bytes) => {
  if (!bytes || bytes <= 0) return '0 MB'
  const mb = bytes / (1024 * 1024)
  return mb >= 0.1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`
}

const LEAVE_PAGE_MESSAGE =
  'Make sure you save your interview before you leave this page.'

const InterviewModal = ({
  title,
  message,
  confirmLabel = 'OK',
  onConfirm,
  cancelLabel,
  onCancel,
  variant = 'info'
}) => {
  const accent =
    variant === 'warning'
      ? 'bg-amber-100 text-amber-700'
      : variant === 'success'
        ? 'bg-emerald-100 text-emerald-700'
        : 'bg-blue-100 text-blue-700'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="interview-modal-title"
    >
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex justify-center">
          <div className={`flex h-12 w-12 items-center justify-center rounded-full ${accent}`}>
            {variant === 'warning' ? (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M12 3l9.5 16.5H2.5L12 3z" />
              </svg>
            ) : (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
              </svg>
            )}
          </div>
        </div>
        <h3 id="interview-modal-title" className="mb-2 text-center text-lg font-semibold text-slate-900">
          {title}
        </h3>
        <p className="mb-6 text-center text-sm leading-relaxed text-slate-600">{message}</p>
        <div className={`flex gap-2 ${cancelLabel ? 'flex-col sm:flex-row' : ''}`}>
          {cancelLabel && onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="w-full rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              {cancelLabel}
            </button>
          )}
          <button
            type="button"
            onClick={onConfirm}
            className="w-full rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

const JobInterviewPage = () => {
  const { applicationId } = useParams()
  const navigate = useNavigate()
  const pageTopRef = useRef(null)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [summary, setSummary] = useState(null)
  const [questionData, setQuestionData] = useState(null)
  const [statusMessage, setStatusMessage] = useState('')

  const [isThinking, setIsThinking] = useState(false)
  const [thinkingSecondsLeft, setThinkingSecondsLeft] = useState(0)

  const [isRecording, setIsRecording] = useState(false)
  const [recordSecondsLeft, setRecordSecondsLeft] = useState(0)
  const [mediaError, setMediaError] = useState('')

  const [previewUrl, setPreviewUrl] = useState('')
  const [recordingReady, setRecordingReady] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const [showSubmitWarning, setShowSubmitWarning] = useState(false)
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(null)
  const [showLeaveWarning, setShowLeaveWarning] = useState(null)
  const [showResetWarning, setShowResetWarning] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [recordedSizeBytes, setRecordedSizeBytes] = useState(0)

  const interviewSubmittedRef = useRef(false)
  const { registerGuard, unregisterGuard } = useInterviewLeaveGuard() || {}

  const streamRef = useRef(null)
  const mediaRecorderRef = useRef(null)
  const chunksRef = useRef([])
  const videoRef = useRef(null)
  const recordedBlobRef = useRef(null)
  const previewUrlRef = useRef('')

  const fetchQuestion = async ({ order } = {}) => {
    const base = getAPIBaseURL()
    const url = order
      ? `${base}/employme/jobseeker/interview/applications/${applicationId}/current?order=${encodeURIComponent(
          String(order)
        )}`
      : `${base}/employme/jobseeker/interview/applications/${applicationId}/current`
    return await getData(url, {}, {})
  }

  const resetRecordingState = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop()
      } catch {
        // ignore
      }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current)
      previewUrlRef.current = ''
    }
    chunksRef.current = []
    recordedBlobRef.current = null
    mediaRecorderRef.current = null
    setRecordedSizeBytes(0)
    setPreviewUrl('')
    setRecordingReady(false)
    setIsRecording(false)
    setRecordSecondsLeft(0)
    setMediaError('')
  }, [])

  const applyQuestionData = useCallback((qData, { scrollToTop = false } = {}) => {
    const total = qData.totalQuestions ?? qData.question?.totalQuestions ?? 0
    const answered = qData.answeredCount ?? 0
    const allDone = qData.done || (total > 0 && answered >= total)

    const normalized = {
      ...qData,
      totalQuestions: total || qData.totalQuestions,
      answeredCount: answered,
      done: allDone
    }

    setQuestionData(normalized)
    setSummary((prev) => ({
      ...(prev || {}),
      answeredCount: normalized.answeredCount,
      questionCount: normalized.totalQuestions || prev?.questionCount
    }))

    const thinkingTime = qData.config?.thinkingTimeSeconds ?? 0
    if (thinkingTime > 0 && !qData.answer?.videoUrl && !qData.done) {
      setIsThinking(true)
      setThinkingSecondsLeft(thinkingTime)
    } else {
      setIsThinking(false)
      setThinkingSecondsLeft(0)
    }

    if (scrollToTop) {
      window.requestAnimationFrame(() => {
        pageTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    }
  }, [])

  const loadQuestionByOrder = useCallback(async (order, { scrollToTop = false } = {}) => {
    const qRes = await fetchQuestion(order != null ? { order } : {})
    if (qRes.error || !qRes.result || qRes.result.status !== 200) {
      throw new Error(qRes.result?.data?.data || 'Unable to load question')
    }
    let qData = qRes.result.data.data

    resetRecordingState()
    applyQuestionData(qData, { scrollToTop })
    return qData
  }, [applicationId, applyQuestionData, resetRecordingState])

  useEffect(() => {
    let cancelled = false

    const fetchInitial = async () => {
      setLoading(true)
      setError('')
      setStatusMessage('')
      try {
        const base = getAPIBaseURL()
        const summaryRes = await getData(
          `${base}/employme/jobseeker/interview/applications/${applicationId}`,
          {},
          {}
        )
        if (cancelled) return
        if (summaryRes.error || !summaryRes.result || summaryRes.result.status !== 200) {
          setError(summaryRes.result?.data?.data || 'Unable to load interview')
          return
        }
        const summaryData = summaryRes.result.data.data
        setSummary(summaryData)
        if (summaryData.isExpired) {
          setStatusMessage('This interview has expired and can no longer be completed.')
          return
        }

        const answeredCount = summaryData.answeredCount || 0
        const totalQuestions = summaryData.questionCount || 0
        const allAnswered = totalQuestions > 0 && answeredCount >= totalQuestions

        if (totalQuestions === 0) {
          setError('No interview questions are configured for this job.')
          return
        }

        if (allAnswered) {
          await loadQuestionByOrder(totalQuestions)
        } else {
          await loadQuestionByOrder(null)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Unable to load interview')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchInitial()

    return () => {
      cancelled = true
    }
  }, [applicationId, loadQuestionByOrder])

  useEffect(() => {
    if (!isThinking) return
    if (thinkingSecondsLeft <= 0) {
      setIsThinking(false)
      return
    }
    const id = setTimeout(() => {
      setThinkingSecondsLeft((s) => s - 1)
    }, 1000)
    return () => clearTimeout(id)
  }, [isThinking, thinkingSecondsLeft])

  useEffect(() => {
    if (!isRecording) return
    if (recordSecondsLeft <= 0) {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        try {
          mediaRecorderRef.current.requestData()
        } catch {
          // ignore
        }
        mediaRecorderRef.current.stop()
      }
      return
    }
    const id = setTimeout(() => {
      setRecordSecondsLeft((s) => s - 1)
    }, 1000)
    return () => clearTimeout(id)
  }, [isRecording, recordSecondsLeft])

  useEffect(() => {
    return () => {
      resetRecordingState()
    }
  }, [resetRecordingState])

  const isInterviewInProgress =
    !!questionData &&
    summary?.interviewStatus !== 'COMPLETED' &&
    !interviewSubmittedRef.current

  const shouldWarnOnLeave = isInterviewInProgress || (recordingReady && !uploading)

  const requestLeave = useCallback(
    (pending) => {
      if (recordingReady && !uploading) {
        setShowUnsavedWarning(pending)
        return
      }
      if (isInterviewInProgress) {
        setShowLeaveWarning(pending)
        return
      }
      if (pending?.action === 'leave' || pending?.action === 'route') {
        if (typeof pending.proceed === 'function') {
          pending.proceed()
        } else {
          navigate('/jobs/applications')
        }
      }
    },
    [recordingReady, uploading, isInterviewInProgress, navigate]
  )

  useEffect(() => {
    if (!registerGuard || !unregisterGuard) return undefined

    registerGuard({
      shouldBlock: () => shouldWarnOnLeave,
      onBlock: (proceedFn) => {
        requestLeave({ action: 'route', proceed: proceedFn })
      }
    })

    return () => unregisterGuard()
  }, [registerGuard, unregisterGuard, shouldWarnOnLeave, requestLeave])

  useEffect(() => {
    if (!shouldWarnOnLeave) return undefined

    const handleBeforeUnload = (event) => {
      event.preventDefault()
      event.returnValue = LEAVE_PAGE_MESSAGE
      return LEAVE_PAGE_MESSAGE
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [shouldWarnOnLeave])

  useEffect(() => {
    if (!isInterviewInProgress) return undefined

    window.history.pushState({ interviewGuard: true }, '')
    const handlePopState = () => {
      window.history.pushState({ interviewGuard: true }, '')
      requestLeave({
        action: 'route',
        proceed: () => {
          interviewSubmittedRef.current = true
          window.history.back()
        }
      })
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [isInterviewInProgress, requestLeave])

  const handleConfirmLeave = () => {
    const pending = showLeaveWarning
    setShowLeaveWarning(null)
    resetRecordingState()
    interviewSubmittedRef.current = true
    if (pending?.action === 'route' && typeof pending.proceed === 'function') {
      pending.proceed()
    } else if (pending?.action === 'leave') {
      navigate('/jobs/applications')
    }
  }

  const startRecording = async () => {
    setMediaError('')
    setError('')
    if (!questionData || questionData.done) return
    if (isThinking) return
    if (questionData?.answer?.videoUrl) return

    try {
      resetRecordingState()

      const stream = await navigator.mediaDevices.getUserMedia(INTERVIEW_MEDIA_CONSTRAINTS)
      streamRef.current = stream

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        try {
          await videoRef.current.play()
        } catch {
          // ignore autoplay failures
        }
      }

      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
        ? 'video/webm;codecs=vp9,opus'
        : MediaRecorder.isTypeSupported('video/webm')
          ? 'video/webm'
          : ''

      const recorderOptions = {
        videoBitsPerSecond: 500_000,
        audioBitsPerSecond: 96_000,
        ...(mimeType ? { mimeType } : {})
      }
      let recorder
      try {
        recorder = new MediaRecorder(stream, recorderOptions)
      } catch {
        recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)
      }

      chunksRef.current = []

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }

      recorder.onstop = () => {
        const blobType = mimeType || 'video/webm'
        const blob = new Blob(chunksRef.current, { type: blobType })
        recordedBlobRef.current = blob.size > 0 ? blob : null
        setRecordedSizeBytes(blob.size)

        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current)
        }

        if (blob.size > 0) {
          const url = URL.createObjectURL(blob)
          previewUrlRef.current = url
          setPreviewUrl(url)
          setRecordingReady(true)
        } else {
          previewUrlRef.current = ''
          setPreviewUrl('')
          setRecordingReady(false)
          setMediaError('Recording was empty. Please try again.')
        }

        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop())
          streamRef.current = null
        }
        if (videoRef.current) {
          videoRef.current.srcObject = null
        }
        setIsRecording(false)
      }

      mediaRecorderRef.current = recorder
      recorder.start(250)

      const limit = questionData.config?.timeLimitSeconds ?? 60
      setRecordSecondsLeft(limit)
      setIsRecording(true)
    } catch (err) {
      setMediaError('Unable to access camera or microphone. Please check your browser permissions.')
    }
  }

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      try {
        mediaRecorderRef.current.requestData()
      } catch {
        // ignore
      }
      mediaRecorderRef.current.stop()
    }
  }

  const handleDiscardRecording = () => {
    resetRecordingState()
    setStatusMessage('')
    setError('')
  }

  const handleUploadAnswer = async () => {
    if (!recordedBlobRef.current || !questionData) {
      setError('Please record an answer first.')
      return
    }
    if (questionData?.answer?.videoUrl) {
      setError('This question is already answered and cannot be answered again.')
      return
    }

    setUploading(true)
    setError('')
    setStatusMessage('')

    try {
      const blob = recordedBlobRef.current
      const mimeType = blob.type || 'video/webm'
      const questionOrder = questionData.question.order
      const base = getAPIBaseURL()
      const answersBase = `${base}/employme/jobseeker/interview/applications/${applicationId}/answers`

      let result = null
      let reqError = ''

      // Prefer direct-to-GCS upload so large videos bypass Vercel's 4.5MB body limit.
      const uploadUrlRes = await postData(
        `${answersBase}/upload-url`,
        {},
        {
          questionOrder,
          mimeType,
          contentLength: blob.size
        }
      )

      const uploadInfo = uploadUrlRes.result?.data?.data

      if (
        uploadUrlRes.error ||
        uploadInfo?.storage !== 'GCS' ||
        !uploadInfo?.uploadUrl ||
        !uploadInfo?.videoKey
      ) {
        setError('Direct video upload is not available. Please try again later.')
        return
      }

      setStatusMessage('Uploading video to storage...')
      const putResponse = await fetch(uploadInfo.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': uploadInfo.mimeType || 'video/webm' },
        body: blob
      })

      if (!putResponse.ok) {
        setError(
          `Upload to storage failed (${putResponse.status}). Your recording is ${formatRecordingSize(blob.size)}. Please try again.`
        )
        return
      }

      setStatusMessage('Saving your answer...')
      const confirmRes = await postData(
        answersBase,
        {},
        {
          questionOrder,
          videoKey: uploadInfo.videoKey,
          mimeType: uploadInfo.mimeType || mimeType,
          storage: 'GCS'
        }
      )
      reqError = confirmRes.error
      result = confirmRes.result

      if (reqError || !result || (result.status !== 200 && result.status !== 201)) {
        if (result?.status === 413) {
          setError(
            result?.data?.data ||
              `Your video (${formatRecordingSize(blob.size)}) is too large to upload. Try a shorter answer.`
          )
        } else if (reqError && !result) {
          setError(
            `Upload failed. Your recording is ${formatRecordingSize(blob.size)} — it may exceed the server upload limit. Try a shorter answer or re-record.`
          )
        } else {
          setError(result?.data?.data || 'Unable to save your answer')
        }
        return
      }

      const saved = result.data?.data || result.data || {}
      const total = Number(saved.totalQuestions || questionData?.totalQuestions || summary?.questionCount || 0)
      const savedOrder = Number(saved.savedQuestionOrder || questionData?.question?.order || 0)
      const answered = Number(saved.answeredCount ?? 0)

      resetRecordingState()

      setSummary((prev) => ({
        ...(prev || {}),
        answeredCount: answered,
        questionCount: total || prev?.questionCount
      }))

      const isComplete =
        Boolean(saved.done) ||
        (total > 0 && answered >= total) ||
        (total > 0 && savedOrder >= total)

      const nextOrder = saved.nextQuestionOrder != null ? Number(saved.nextQuestionOrder) : null

      if (isComplete) {
        const lastOrder = savedOrder || total
        try {
          const qData = await loadQuestionByOrder(lastOrder, { scrollToTop: true })
          applyQuestionData(
            { ...qData, done: true, answeredCount: total || answered, totalQuestions: total },
            { scrollToTop: true }
          )
        } catch (loadErr) {
          applyQuestionData(
            {
              ...questionData,
              done: true,
              answeredCount: total || answered,
              totalQuestions: total,
              answer: { videoUrl: saved.videoUrl, takesUsed: saved.takesUsed }
            },
            { scrollToTop: true }
          )
        }
        setStatusMessage(`Answer saved. You've completed all ${total || ''} questions!`)
      } else if (nextOrder && nextOrder !== savedOrder) {
        try {
          await loadQuestionByOrder(nextOrder, { scrollToTop: true })
          setStatusMessage(`Answer saved. Moving to question ${nextOrder} of ${total || ''}.`)
        } catch (loadErr) {
          setError(loadErr?.message || 'Answer saved, but unable to load the next question.')
        }
      } else if (savedOrder > 0 && savedOrder < total) {
        try {
          await loadQuestionByOrder(savedOrder + 1, { scrollToTop: true })
          setStatusMessage(`Answer saved. Moving to question ${savedOrder + 1} of ${total || ''}.`)
        } catch (loadErr) {
          setError(loadErr?.message || 'Answer saved, but unable to load the next question.')
        }
      } else {
        setError('Answer saved, but unable to determine the next question. Please reload the page.')
      }
    } catch (err) {
      setError(err?.message || 'Unable to upload video. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const handleReloadQuestion = async () => {
    if (!applicationId) return
    if (recordingReady && !uploading) {
      setShowUnsavedWarning({ action: 'reload' })
      return
    }
    setLoading(true)
    setError('')
    setStatusMessage('')
    try {
      const order = questionData?.question?.order
      await loadQuestionByOrder(order != null ? order : null)
    } catch (err) {
      setError(err?.message || 'Unable to reload question.')
    } finally {
      setLoading(false)
    }
  }

  const handleGoToQuestionOrder = async (nextOrder) => {
    if (!applicationId) return
    if (recordingReady && !uploading) {
      setShowUnsavedWarning({ action: 'navigate', order: nextOrder })
      return
    }
    setLoading(true)
    setError('')
    setStatusMessage('')
    try {
      await loadQuestionByOrder(nextOrder, { scrollToTop: true })
    } catch (err) {
      setError(err?.message || 'Unable to load question.')
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmUnsavedNavigation = async () => {
    const pending = showUnsavedWarning
    setShowUnsavedWarning(null)
    resetRecordingState()
    setLoading(true)
    setError('')
    setStatusMessage('')
    try {
      if (pending?.action === 'reload') {
        const order = questionData?.question?.order
        await loadQuestionByOrder(order != null ? order : null)
      } else if (pending?.action === 'navigate' && pending.order != null) {
        await loadQuestionByOrder(pending.order, { scrollToTop: true })
      } else if (pending?.action === 'leave') {
        interviewSubmittedRef.current = true
        navigate('/jobs/applications')
      } else if (pending?.action === 'route' && typeof pending.proceed === 'function') {
        interviewSubmittedRef.current = true
        pending.proceed()
      } else if (pending?.action === 'reset') {
        setShowResetWarning(true)
      }
    } catch (err) {
      setError(err?.message || 'Unable to load question.')
    } finally {
      setLoading(false)
    }
  }

  const handleResetInterview = () => {
    if (recordingReady && !uploading) {
      setShowUnsavedWarning({ action: 'reset' })
      return
    }
    setShowResetWarning(true)
  }

  const handleConfirmResetInterview = async () => {
    setShowResetWarning(false)
    setResetting(true)
    setError('')
    setStatusMessage('')
    resetRecordingState()
    try {
      const base = getAPIBaseURL()
      const { error: reqError, result } = await postData(
        `${base}/employme/jobseeker/interview/applications/${applicationId}/reset`,
        {},
        {}
      )
      if (reqError || !result || result.status !== 200) {
        setError(result?.data?.data || 'Unable to reset interview')
        return
      }
      setSummary((prev) => (prev ? { ...prev, answeredCount: 0 } : { answeredCount: 0 }))
      await loadQuestionByOrder(1, { scrollToTop: true })
      setStatusMessage('Interview reset. You can start again from question 1.')
    } catch (err) {
      setError('Unable to reset interview. Please try again.')
    } finally {
      setResetting(false)
    }
  }

  const handleSubmitInterview = async () => {
    const totalQuestions = summary?.questionCount || questionData?.totalQuestions || 0
    const answeredCount =
      summary?.answeredCount ??
      questionData?.answeredCount ??
      (questionData?.done ? totalQuestions : 0)

    if (totalQuestions > 0 && answeredCount < totalQuestions) {
      setShowSubmitWarning(true)
      return
    }

    setSubmitting(true)
    setError('')
    setStatusMessage('')
    try {
      const base = getAPIBaseURL()
      const { error: reqError, result } = await postData(
        `${base}/employme/jobseeker/interview/applications/${applicationId}/submit`,
        {},
        appendReferralAttribution({})
      )
      if (reqError || !result || result.status !== 200) {
        const message = result?.data?.data || 'Unable to submit your interview'
        if (message.toLowerCase().includes('answer all')) {
          setShowSubmitWarning(true)
        } else {
          setError(message)
        }
        return
      }
      setStatusMessage('Interview submitted successfully.')
      interviewSubmittedRef.current = true
      navigate('/jobs/applications')
    } catch (err) {
      setError('Unable to submit your interview. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading && !questionData) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <p className="text-sm text-slate-600">Loading interview...</p>
      </div>
    )
  }

  if (error && !questionData) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <p className="text-sm text-red-600 mb-4">{error}</p>
        <button
          type="button"
          onClick={() => navigate('/jobs/applications')}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Back to applications
        </button>
      </div>
    )
  }

  if (!loading && !questionData) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <p className="text-sm text-slate-600 mb-4">
          {statusMessage || 'Unable to load interview questions.'}
        </p>
        <button
          type="button"
          onClick={() => navigate('/jobs/applications')}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Back to applications
        </button>
      </div>
    )
  }

  const totalQuestions = questionData?.totalQuestions || summary?.questionCount || 0
  const answeredCount = Math.min(
    totalQuestions,
    questionData?.answeredCount ?? summary?.answeredCount ?? 0
  )
  const allQuestionsAnswered =
    questionData?.done || (totalQuestions > 0 && answeredCount >= totalQuestions)
  const currentOrder = Number(questionData?.question?.order) || 1
  const maxTakesPerQuestion = questionData?.config?.maxTakesPerQuestion || 1
  const takesUsed = questionData?.answer?.takesUsed || 0
  const canRecordMore = takesUsed < maxTakesPerQuestion
  const isAnswered = !!questionData?.answer?.videoUrl
  const canNavigatePrev = totalQuestions > 0 && currentOrder > 1
  const canNavigateNext = totalQuestions > 0 && currentOrder < totalQuestions
  const progressSteps = allQuestionsAnswered
    ? totalQuestions
    : isAnswered
      ? Math.max(answeredCount, currentOrder)
      : Math.max(answeredCount, currentOrder - 1)
  const progressPercent =
    totalQuestions > 0 ? Math.min(100, Math.round((progressSteps / totalQuestions) * 100)) : 0
  const canStartRecording =
    !isRecording && !isThinking && canRecordMore && !isAnswered && !allQuestionsAnswered
  const showQuestion = !!questionData?.question

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" ref={pageTopRef}>
      {showSubmitWarning && (
        <InterviewModal
          title="Incomplete interview"
          message="You must answer all questions before you can submit your interview. Please complete any remaining video answers."
          confirmLabel="Got it"
          variant="warning"
          onConfirm={() => setShowSubmitWarning(false)}
        />
      )}

      {showUnsavedWarning && (
        <InterviewModal
          title="Unsaved recording"
          message="You have a recording that hasn't been saved. If you leave now, it will be discarded."
          confirmLabel="Discard and continue"
          cancelLabel="Keep recording"
          variant="warning"
          onCancel={() => setShowUnsavedWarning(null)}
          onConfirm={handleConfirmUnsavedNavigation}
        />
      )}

      {showLeaveWarning && (
        <InterviewModal
          title="Leave interview?"
          message={LEAVE_PAGE_MESSAGE}
          confirmLabel="Leave anyway"
          cancelLabel="Stay on page"
          variant="warning"
          onCancel={() => setShowLeaveWarning(null)}
          onConfirm={handleConfirmLeave}
        />
      )}

      {showResetWarning && (
        <InterviewModal
          title="Reset interview"
          message="This will clear all your recorded answers and start the interview from question 1. This cannot be undone."
          confirmLabel={resetting ? 'Resetting...' : 'Reset interview'}
          cancelLabel="Cancel"
          variant="warning"
          onCancel={() => !resetting && setShowResetWarning(false)}
          onConfirm={handleConfirmResetInterview}
        />
      )}

      <h1 className="mb-2 text-xl font-semibold leading-snug text-slate-900 sm:text-2xl">Video interview</h1>
      <p className="mb-4 text-sm leading-relaxed text-slate-600">
        Answer each question with a short video. You have {questionData?.config?.timeLimitSeconds || 60} seconds
        per question and up to {maxTakesPerQuestion} take(s).
      </p>

      {totalQuestions > 0 && (
        <div className="mb-4">
          <div className="mb-1 flex items-center justify-between gap-2 text-xs text-slate-600">
            <span className="min-w-0">
              Question {currentOrder} of {totalQuestions} · {answeredCount} answered
            </span>
            <span className="shrink-0 whitespace-nowrap">{progressPercent}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full rounded-full bg-[#00A5E6] transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {summary?.expiresAt && (
        <p className="text-xs text-slate-500 mb-3">
          Interview expiry:{' '}
          {new Date(summary.expiresAt).toLocaleString()}
        </p>
      )}

      {error && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {statusMessage && (
        <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          {statusMessage}
        </div>
      )}
      {mediaError && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {mediaError}
        </div>
      )}

      {showQuestion && (
        <div className="mb-4 flex items-center gap-2 text-xs text-slate-600">
          <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 font-medium text-blue-800">
            Question {currentOrder} of {totalQuestions}
          </span>
          {isAnswered && (
            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 font-medium text-emerald-800">
              Answered
            </span>
          )}
          {recordingReady && !isAnswered && (
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 font-medium text-amber-800">
              Ready to save
            </span>
          )}
        </div>
      )}

      {allQuestionsAnswered && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          You've answered all questions ({totalQuestions}). Review your answers using Previous/Next if needed, then submit your interview.
        </div>
      )}

      {showQuestion && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 mb-4">
          <p className="text-sm font-medium text-slate-900 mb-2">Question</p>
          <p className="text-sm text-slate-700 whitespace-pre-line">
            {questionData?.question?.text || 'Question not available.'}
          </p>
        </div>
      )}

      {showQuestion && isAnswered && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          This question is already answered and is locked. Use Next/Previous to navigate.
        </div>
      )}

      {showQuestion && !allQuestionsAnswered && (
      <div className="rounded-lg border border-slate-200 bg-white p-4 mb-4">
        <p className="text-sm font-medium text-slate-900 mb-2">Recording</p>
        <div className="mb-3">
          <div className="relative aspect-video w-full max-w-md bg-black rounded-md overflow-hidden border border-slate-200">
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              autoPlay
              muted
              playsInline
            />
            {isRecording && (
              <div className="absolute top-2 left-2 flex items-center gap-1.5 rounded-full bg-red-600/90 px-2 py-1 text-xs font-medium text-white">
                <span className="h-2 w-2 animate-pulse rounded-full bg-white" />
                REC {recordSecondsLeft}s
              </div>
            )}
            {isThinking && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                <p className="text-white text-sm font-medium">
                  Prepare your answer — {thinkingSecondsLeft}s
                </p>
              </div>
            )}
          </div>
        </div>

        {isThinking && (
          <p className="text-xs text-slate-600 mb-2">
            Thinking time: {thinkingSecondsLeft}s remaining before you can record.
          </p>
        )}
        {isRecording && (
          <p className="text-xs text-slate-600 mb-2">
            Recording in progress. Click Stop when you're finished, then save to move to the next question.
          </p>
        )}
        {!isRecording && !isThinking && !recordingReady && !isAnswered && (
          <p className="text-xs text-slate-600 mb-2">
            When you are ready, click &quot;Start recording&quot;. Your camera and microphone will be used.
          </p>
        )}
        {recordingReady && !isAnswered && (
          <p className="text-xs text-amber-700 mb-2">
            Review your recording below, then click &quot;Save &amp; next question&quot; to continue.
          </p>
        )}

        <div className="flex flex-wrap gap-2 mt-2">
          <button
            type="button"
            disabled={!canStartRecording}
            onClick={startRecording}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {isAnswered ? 'Answered' : (canRecordMore ? 'Start recording' : 'Max takes used')}
          </button>
          <button
            type="button"
            disabled={!isRecording}
            onClick={handleStopRecording}
            className="rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-60"
          >
            Stop
          </button>
          <button
            type="button"
            disabled={!recordingReady || uploading || isAnswered}
            onClick={handleUploadAnswer}
            className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            {uploading ? 'Saving...' : 'Save & next question'}
          </button>
          {recordingReady && !isAnswered && (
            <button
              type="button"
              disabled={uploading}
              onClick={handleDiscardRecording}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              Re-record
            </button>
          )}
          <button
            type="button"
            disabled={loading || uploading}
            onClick={handleReloadQuestion}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            Reload question
          </button>
        </div>

        <p className="mt-2 text-xs text-slate-600">
          Takes used for this question: {takesUsed} / {maxTakesPerQuestion}
        </p>
      </div>
      )}

      {previewUrl && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 mb-4">
          <p className="text-sm font-medium text-slate-900 mb-1">Preview your answer</p>
          {recordedSizeBytes > 0 && (
            <p className="text-xs text-slate-500 mb-2">File size: {formatRecordingSize(recordedSizeBytes)}</p>
          )}
          <video src={previewUrl} controls className="w-full max-w-md rounded-md border border-slate-200" />
        </div>
      )}

      {showQuestion && (
        <div className="flex items-center justify-between gap-2 mb-4">
          <button
            type="button"
            disabled={!canNavigatePrev || loading || uploading}
            onClick={() => handleGoToQuestionOrder(currentOrder - 1)}
            className="rounded-md border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            Previous question
          </button>
          <button
            type="button"
            disabled={!canNavigateNext || loading || uploading}
            onClick={() => handleGoToQuestionOrder(currentOrder + 1)}
            className="rounded-md border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            Next question
          </button>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 mt-4">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => requestLeave({ action: 'leave' })}
            className="rounded-md border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            Back to applications
          </button>
          <button
            type="button"
            onClick={handleResetInterview}
            disabled={resetting || uploading || submitting || summary?.interviewStatus === 'COMPLETED'}
            className="rounded-md border border-red-300 px-4 py-2 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
          >
            {resetting ? 'Resetting...' : 'Reset interview'}
          </button>
        </div>
        <button
          type="button"
          onClick={handleSubmitInterview}
          disabled={submitting || !allQuestionsAnswered}
          title={!allQuestionsAnswered ? 'Answer all questions before submitting' : ''}
          className="rounded-md bg-indigo-600 px-4 py-2 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
        >
          {submitting ? 'Submitting...' : 'Submit interview'}
        </button>
      </div>

      {!allQuestionsAnswered && (
        <p className="mt-2 text-xs text-slate-500 text-right">
          Submit will be available after all {totalQuestions} questions are answered.
        </p>
      )}
    </div>
  )
}

export default JobInterviewPage
